import { useRouteAndTeamInfo } from "@/utils/hooks/url/use-route-info";
import { useDANGEROUSisChalkEmployee } from "@/utils/hooks/use-is-chalk-employee";
import { ConnectError } from "@connectrpc/connect";

export interface UseIsServerlessEnvironmentResult {
  /**
   * True only once RouteAndTeamInfo has loaded successfully AND the current environment is a
   * Chalk serverless deployment. It is `false` while loading and `false` on error, so a
   * consumer that reads only this boolean safely treats both "not yet known" and "failed to
   * load" as "not serverless" (i.e. does not gate). Consumers that must distinguish those
   * cases (e.g. to show an error) should read `loading` / `error`.
   */
  isServerless: boolean;
  /** True only while the team request is genuinely in flight (never true on error). */
  loading: boolean;
  /** The team-load error, if the RouteAndTeamInfo request failed. */
  error?: ConnectError;
}

/**
 * Reads whether the current environment is a Chalk "serverless" deployment.
 *
 * Determining this requires the RouteAndTeamInfo (team) request to resolve successfully, so
 * `isServerless` is `false` until then and `false` on error — an error means we never have a
 * definitive "serverless" answer, so gated UI is never revealed as serverless on a failure.
 * `loading` and `error` are surfaced separately so callers can react to each: the
 * {@link ChalkServerlessGuard} component renders the error rather than deferring to its
 * children, while raw-boolean consumers (e.g. navbar link assembly) can simply treat error as
 * "not serverless".
 */
export function useIsServerlessEnvironment(): UseIsServerlessEnvironmentResult {
  const { isServerlessEnvironment, loading, error } = useRouteAndTeamInfo();
  return {
    isServerless: !loading && error == null && (isServerlessEnvironment ?? false),
    loading,
    error,
  };
}

export interface UseCanViewServerlessGatedUIResult {
  /**
   * Whether UI wrapped in {@link ChalkServerlessGuard} is rendered at all. False while the
   * environment is loading and for regular users in serverless environments; true for hosted
   * environments and chalk admins (who see it inside the guard's admin visual wrapper). A
   * team-load error counts as visible so the guard can surface the error instead of the UI
   * silently vanishing.
   */
  canView: boolean;
  /** True while the environment is unknown, so callers can render a loading state instead. */
  loading: boolean;
}

/**
 * For callers that must decide before rendering whether guarded UI exists at all, e.g. whether
 * to include a tab. `canView` is false while `loading`, so callers that ignore `loading` simply
 * hide the UI until the environment resolves.
 */
export function useCanViewServerlessGatedUI(): UseCanViewServerlessGatedUIResult {
  const { isServerless, loading } = useIsServerlessEnvironment();
  const isChalkAdmin = useDANGEROUSisChalkEmployee();
  return { canView: !loading && (!isServerless || isChalkAdmin), loading };
}
